import {
  CurrencyConversionRateWithDate,
  FeeTypeSlug,
  PriceAmount,
  PriceAmountWithConversions,
  PriceAmountWithDetails,
  PriceAmountWithDetailsWithConversions,
} from '@/api';
import { ArticleWithRelations, RequestWithRelations } from '@/models/request';
import { Currency } from '@/utils/number';

type ConversionWithDetails = {
  amount: PriceAmountWithDetails;
  rate: CurrencyConversionRateWithDate;
  currency: Currency;
  noVATApplied: boolean;
};

export const getConversionPrices = (
  price: PriceAmountWithDetailsWithConversions | PriceAmountWithDetails | PriceAmount
): ConversionWithDetails[] => {
  if (!('conversions' in price) || !price.conversions) {
    return [];
  }

  const { conversions, details, noVATApplied } = price;

  return Object.entries(conversions).map(([currency, conversion]) => {
    const convertedDetails = details
      ? details
          .map((detail) => {
            const convertedAmount = detail.conversions?.[currency as Currency]?.amount;

            if (!convertedAmount) {
              return null;
            }

            return {
              ...convertedAmount,
              type: detail.type,
              ...('subType' in detail ? { subType: detail.subType } : {}),
              ...('rate' in detail ? { rate: detail.rate } : {}),
            };
          })
          .filter((detail) => !!detail)
      : [];

    return {
      amount: {
        ...conversion.amount,
        details: convertedDetails,
      },
      rate: conversion.rate,
      currency: currency as Currency,
      noVATApplied,
    } as ConversionWithDetails;
  });
};

const sumPriceAmounts = (priceAmounts: PriceAmount[]): PriceAmount => {
  if (!priceAmounts.length) {
    throw new Error('No priceAmounts to sum');
  }

  const noVATApplied = priceAmounts.some((priceAmount) => priceAmount.noVATApplied === true);

  const currency = priceAmounts[0].currency;
  const amountBeforeTaxes = priceAmounts.reduce(
    (acc, priceAmount) => acc + priceAmount.amountBeforeTaxes,
    0
  );
  const amount = priceAmounts.reduce((acc, priceAmount) => acc + priceAmount.amount, 0);

  const taxes: PriceAmount['taxes'] = [];

  for (const priceAmount of priceAmounts) {
    for (const priceAmountTax of priceAmount.taxes) {
      const tax = taxes.find(({ rate }) => rate === priceAmountTax.rate);

      if (tax) {
        tax.amount += priceAmountTax.amount;
      } else {
        taxes.push({ ...priceAmountTax });
      }
    }
  }

  return {
    currency,
    amountBeforeTaxes,
    taxes: taxes.filter((tax) => tax.amount !== 0),
    amount,
    noVATApplied,
  };
};

const sumPriceAmountsWithConversions = (
  priceAmountsWithConversions: PriceAmountWithConversions[]
): PriceAmountWithConversions => {
  return {
    ...sumPriceAmounts(priceAmountsWithConversions),
    conversions: priceAmountsWithConversions.reduce(
      (acc, curr) => {
        for (const [currency, { rate, amount }] of Object.entries(curr.conversions)) {
          const existingAmount = acc[currency as Currency]?.amount;

          acc[currency as Currency] = {
            rate,
            amount: existingAmount ? sumPriceAmounts([existingAmount, amount]) : amount,
          };
        }

        return acc;
      },
      {} as PriceAmountWithConversions['conversions']
    ),
  };
};

export const getFeesGroupedByType = (
  request: RequestWithRelations,
  type: 'organization' | 'workshop',
  workshopId?: string
): { type: FeeTypeSlug; price: PriceAmountWithConversions }[] => {
  const allFees = [
    ...request.fees,
    ...request.allArticles.flatMap((article) => article.snapshot.fees),
  ];

  const feesByType = allFees.reduce(
    (acc, curr) => {
      const currType = curr.feeType.slug;
      const currPrice =
        type === 'organization'
          ? curr.organizationPrice?.amountPerCurrency?.[0]
          : workshopId
            ? curr.workshopPrice?.amountPerEntity?.find(({ entityId }) => entityId === workshopId)
            : curr.workshopPrice?.amountPerCurrency?.[0];

      if (!currPrice) {
        return acc;
      }

      const existingType = acc.find((accElement) => accElement.type === currType);

      if (!existingType) {
        return [
          ...acc,
          {
            type: currType,
            price: currPrice,
          },
        ];
      }

      existingType.price = sumPriceAmountsWithConversions([existingType.price, currPrice]);

      return acc;
    },
    [] as { type: FeeTypeSlug; price: PriceAmountWithConversions }[]
  );

  return feesByType;
};

export type NullablePriceAmount = {
  amount: number | null;
  amountBeforeTaxes: number | null;
  taxes: { rate: number; amount: number }[];
  currency: Currency;
};

/**
 * Recomputes a price amount, based on a new amount, amountBeforeTaxes, or taxes.
 */
export const recomputePrice = (
  previousPrice: NullablePriceAmount,
  newPrice: Partial<NullablePriceAmount>
): NullablePriceAmount => {
  if (newPrice.amount === null || newPrice.amountBeforeTaxes === null) {
    const taxRate = previousPrice.taxes?.[0]?.rate ?? 0;

    return {
      ...previousPrice,
      amount: null,
      amountBeforeTaxes: null,
      taxes: [{ rate: taxRate, amount: 0 }],
    };
  }

  if (newPrice.amount) {
    const taxRate = previousPrice.taxes?.[0]?.rate ?? 0;
    const taxAmount = Math.round(newPrice.amount - newPrice.amount / (1 + taxRate / 100));
    const amountBeforeTaxes = newPrice.amount - taxAmount;

    return {
      ...previousPrice,
      amount: newPrice.amount,
      taxes: [{ rate: taxRate, amount: taxAmount }],
      amountBeforeTaxes,
    };
  }

  if (newPrice.amountBeforeTaxes) {
    const taxRate = previousPrice.taxes?.[0]?.rate ?? 0;
    const taxAmount = Math.round(newPrice.amountBeforeTaxes * (taxRate / 100));
    const amount = newPrice.amountBeforeTaxes + taxAmount;

    return {
      ...previousPrice,
      amount,
      taxes: [{ rate: taxRate, amount: taxAmount }],
      amountBeforeTaxes: newPrice.amountBeforeTaxes,
    };
  }

  if (newPrice.taxes) {
    const taxRate = newPrice.taxes[0]?.rate ?? 0;

    if (previousPrice.amountBeforeTaxes === null) {
      return {
        ...previousPrice,
        taxes: [{ rate: taxRate, amount: 0 }],
      };
    }

    const taxAmount = Math.round(previousPrice.amountBeforeTaxes * (taxRate / 100));
    const amount = previousPrice.amountBeforeTaxes + taxAmount;

    return {
      ...previousPrice,
      amount,
      taxes: [{ rate: taxRate, amount: taxAmount }],
    };
  }

  return previousPrice;
};

export const shouldShowPendingWorkshopQuote = (article: ArticleWithRelations): boolean =>
  !!article.workshopId &&
  !!article.steps?.some(
    (step) =>
      ['in-progress', 'pending'].includes(step.status) &&
      ['analysis', 'processing'].includes(step.step)
  ) &&
  !!article.snapshot.serviceChoice &&
  ['care-repair', 'exchange'].includes(article.snapshot.serviceChoice);

export const shouldShowPendingOrganizationQuote = (article: ArticleWithRelations): boolean =>
  !!article.steps?.some(
    (step) =>
      ['in-progress', 'pending'].includes(step.status) &&
      ['analysis', 'processing'].includes(step.step)
  ) &&
  !!article.snapshot.serviceChoice &&
  ['care-repair', 'exchange'].includes(article.snapshot.serviceChoice);
